import React, { useState } from "react";
import { DailyReport } from "../../../../components/reports/DailyReport";
import { FinalReport } from "../../../../components/reports/FinalReport";
import { Asset, ProhibitionRequest, Referral, Report } from "types";
import { useTooltip } from "context/TooltipProvider";

export const ProhibitionReportTabs: React.FC<{
  referralId?: string;
  assets?: Asset[];
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
  onNewDataAdded?: (data: any) => void;
  referral: Referral | null;
  assetsNeedingReport: Asset[];
  reports?: Report[];
  recoveryReports?: Report[];
  suspectAssetMappings?: any[];
  suspectAssetMappingsNeedingReport?: any[];
  prohibitionRecord: ProhibitionRequest[] | null;
}> = ({
  referralId,
  assets,
  onNewDataAdded,
  setActiveTab,
  referral,
  assetsNeedingReport,
  reports = [],
  suspectAssetMappings = [],
  suspectAssetMappingsNeedingReport = [],
  prohibitionRecord,
}) => {
  const [activeReportTab, setActiveReportTab] = useState<"daily" | "final">(
    "daily",
  );
  const [isFinalVerdict, setIsFinalVerdict] = useState(false);
  const { showTooltip } = useTooltip();

  // Check if at least one daily report exists (is_next_appointment_reason_final_verdict is false)
  const hasDailyReports = () => {
    const dailyProhibitionReports = reports.filter(
      (report) => report.is_next_appointment_reason_final_verdict === false,
    );

    return dailyProhibitionReports.length > 0;
  };

  const handleIsFinalVerdictSwitch = () => {
    if (!isFinalVerdict) {
      // Check if at least one daily report exists
      if (!hasDailyReports()) {
        showTooltip(
          "Please add at least one daily report before creating a final report",
          "info",
        );
        return;
      }

      // Check for null verdicts
      const hasNullVerdicts = assets?.some(
        (asset) => asset.prohabition_court_verdict === null,
      );

      if (hasNullVerdicts) {
        showTooltip(
          "Please fill court verdict for all assets before final report",
          "info",
        );
        return;
      }
    }

    setIsFinalVerdict(!isFinalVerdict);
    if (!isFinalVerdict) {
      setActiveReportTab("final");
    } else {
      setActiveReportTab("daily");
    }
  };

  return (
    <div className=" bg-white shadow-md rounded-b-lg rounded-r-lg pb-4 pt-2 mt-2">
      {/* Tab Headers */}
      <div className="flex items-center justify-between mb-2 mt-3 ml-2 pr-4">
        <div className="flex space-x-4">
          <button
            onClick={() => setActiveReportTab("daily")}
            disabled={isFinalVerdict}
            className={`px-4 rounded-t-lg transition-colors ${
              activeReportTab === "daily"
                ? "referral-active-tab"
                : isFinalVerdict
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed opacity-60"
                  : "referral-inactive-tab hover:bg-gray-100 bg-gray-200 text-gray-400"
            }`}
          >
            Add Report
          </button>

          <button
            onClick={() => setActiveReportTab("final")}
            disabled={!isFinalVerdict}
            className={`px-4 rounded-t-lg transition-colors ${
              activeReportTab === "final"
                ? "referral-active-tab"
                : !isFinalVerdict
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed opacity-60"
                  : "referral-inactive-tab hover:bg-gray-100 bg-gray-200 text-gray-400 "
            }`}
          >
            Add Final Report
          </button>
        </div>

        {/* Final Verdict Switch */}
        <div className="flex items-center space-x-2 relative ml-2">
          <span className="text-sm text-gray-600">Is Final Verdict</span>
          <button
            type="button"
            onClick={handleIsFinalVerdictSwitch}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              isFinalVerdict ? "bg-blue-600" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                isFinalVerdict ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-1">
        {activeReportTab === "daily" ? (
          <DailyReport
            referralId={referralId}
            setActiveTab={setActiveTab}
            onNewDataAdded={onNewDataAdded}
            referral={referral}
            assets={assets as Asset[]}
            prohibitionRecord={prohibitionRecord}
          />
        ) : (
          <FinalReport
            referralId={referralId}
            setActiveTab={setActiveTab}
            onNewDataAdded={onNewDataAdded}
            referral={referral}
            assets={suspectAssetMappings}
            assetsNeedingReport={suspectAssetMappingsNeedingReport}
            prohibitionRecord={prohibitionRecord}
          />
        )}
      </div>
    </div>
  );
};
